import { Link } from '@tanstack/react-router'
import { MessageSquare } from 'lucide-react'
import { APP_NAME, APP_VERSION } from '@/lib/constants'
import { useAuth } from '@/hooks/use-auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function HelpLayout({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  const { currentUser, signOut } = useAuth()

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-slate-900" />
            <span className="font-semibold text-xl">{APP_NAME}</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/app" className="text-slate-600 hover:text-slate-900">
              Open App
            </Link>
            <Link
              to="/app/help"
              className="text-slate-900 font-semibold"
            >
              Help Center
            </Link>
            {currentUser ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:border-slate-400 hover:text-slate-900"
                  >
                    {currentUser.email}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      void signOut()
                    }}
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/sign-in"
                className="text-slate-600 hover:text-slate-900"
              >
                Sign In
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-5xl">
        <div className="mb-8 space-y-2">
          <p className="text-sm uppercase tracking-wide text-slate-500">
            Help Center
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            {title}
          </h1>
          {description ? (
            <p className="text-slate-600 max-w-2xl">{description}</p>
          ) : null}
        </div>
        {children}
      </main>

      <footer className="border-t py-8 bg-white">
        <div className="container mx-auto px-4 text-center text-slate-600 text-sm">
          <p>© 2026 {APP_NAME} - v{APP_VERSION}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
